// CommandHub.tsx - Modern replacement for ribbon stack
import React, { useState, useEffect } from 'react';
import { AgentProfile } from '../../types';
import '../../styles/command-center.css';

interface CommandHubProps {
  agents: AgentProfile[];
  selectedAgent: string;
  onSelectAgent: (agent: string) => void;
  apis: any[];
  applications: any[];
  onActionClick: (action: string) => void;
  isGeneratingTests: boolean;
  isTestRunning: boolean;
  currentScope?: 'agent' | 'api' | 'application';
  onScopeChange?: (scope: 'agent' | 'api' | 'application') => void;
  selectedAPI?: string;
  onSelectAPI?: (api: string) => void;
  selectedApplication?: string;
  onSelectApplication?: (app: string) => void;
}

const CommandHub: React.FC<CommandHubProps> = ({
  agents,
  selectedAgent,
  onSelectAgent,
  apis,
  applications,
  onActionClick,
  isGeneratingTests,
  isTestRunning,
  currentScope = 'agent',
  onScopeChange,
  selectedAPI = '',
  onSelectAPI,
  selectedApplication = '',
  onSelectApplication
}) => {
  //console.log('APIs received in CommandHub:', apis);
  const [activeSection, setActiveSection] = useState<string>('agent');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [theme, setTheme] = useState<'yellow' | 'red' | 'blue' | 'black'>('yellow');

  // Use prop value if provided, otherwise local state
  // Add local state for scope
  const [localScope, setLocalScope] = useState<'agent' | 'api' | 'application'>(currentScope || 'agent');

  // Use local state
  const scopeType = localScope;
  const handleScopeChange = (scope: 'agent' | 'api' | 'application') => {
    setLocalScope(scope);  // Update local state
    if (onScopeChange) {
      onScopeChange(scope);  // Also notify parent if handler exists
    }
  };


  // COMMENTED as WE Are USING PROPS - RAMANA 11.09.2025

  // Mock data - will be replaced with actual API calls
  /*
  const [apis, setApis] = useState([
    { id: '1', name: 'Payment API', testCount: 5 },
    { id: '2', name: 'Customer API', testCount: 3 }
  ]);
  const [applications, setApplications] = useState([
    { id: '1', name: 'BillingSystem', agentCount: 2, apiCount: 3 },
    { id: '2', name: 'CustomerPortal', agentCount: 1, apiCount: 2 }
  ]);
  

  // Fetch real data
  useEffect(() => {
    // Fetch APIs
    fetch(`http://localhost:8000/api/endpoints`)
      .then(res => res.json())
      .then(data => {
        if (data.success && data.endpoints) {
          const transformedApis = data.endpoints.map((api: any) => ({
            id: api.id,
            name: api.name,
            testCount: 0 // TODO: Get actual test count
          }));
          setApis(transformedApis);
        }
      })
      .catch(err => console.error('Failed to fetch APIs:', err));

    // Fetch Applications  
    fetch(`http://localhost:8000/db/applications`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          const transformedApps = data.map((app: any) => ({
            id: app.id,
            name: app.name,
            agentCount: 0, // TODO: Get actual counts
            apiCount: 0
          }));
          setApplications(transformedApps);
        }
      })
      .catch(err => console.error('Failed to fetch applications:', err));
  }, []);
  */

  // Sync local scope with prop changes
  useEffect(() => {
    if (currentScope) {
      setLocalScope(currentScope);
    }
  }, [currentScope]);

  // Dynamic action groups based on context - RESTORED ORIGINAL LOGIC
  /*
  const getContextualActions = () => {
    // Actions and Reports should ALWAYS be visible, not dependent on selection
    if (isTestRunning) {
      return {
        primary: ['stop-tests', 'view-logs'],
        secondary: ['results']
      };
    }

    if (isGeneratingTests) {
      return {
        primary: ['view-progress'],
        secondary: ['scenarios']
      };
    }

    // Default state - always show these actions
    return {
      primary: ['scenarios', 'execute'],
      secondary: ['data', 'report', 'defects']
    };
  };

  const actions = getContextualActions();
*/
  return (
    <div className={`command-hub ${theme}-banner`}>

      {/* Header band (white text) */}
      <div className="ribbon-header">
        <h4>MANAGEMENT</h4>
      </div>

      {/* Collapse Toggle 

      <button
        className="hub-toggle"
        onClick={() => setIsCollapsed(!isCollapsed)}
        title={isCollapsed ? 'Expand' : 'Collapse'}
      >
        {isCollapsed ? '→' : '←'}
      </button>
        */}
      {!isCollapsed && (
        <div className="hub-body">
          {/* 1. APPLICATION MANAGEMENT */}
          <div className="hub-section agent-section">
            <h3 className="hub-title">APPLICATION</h3>

            {/* Scope Selection (labels must be white) backgroundColor: 'rgba(0,0,0,0.05)', */}
            <div className="scope-group" style={{
              display: 'flex',
              gap: '1rem',
              marginBottom: '0.65rem',
              padding: '0.25rem 0.5rem',
              borderRadius: '4px',
              alignItems: 'center'
            }}>
              <label className="scope-label" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="scope"
                  value="agent"
                  checked={scopeType === 'agent'}
                  onChange={() => handleScopeChange('agent')}
                  style={{ transform: 'scale(0.9)' }}
                />
                <span className="scope-text" style={{
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em'
                }}>Agent</span>
              </label>

              <label className="scope-label" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="scope"
                  value="api"
                  checked={scopeType === 'api'}
                  onChange={() => handleScopeChange('api')}
                  style={{ transform: 'scale(0.9)' }}
                />
                <span className="scope-text" style={{
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em'
                }}>API</span>
              </label>

              <label className="scope-label" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="scope"
                  value="application"
                  checked={scopeType === 'application'}
                  onChange={() => handleScopeChange('application')}
                  style={{ transform: 'scale(0.9)' }}
                />
                <span className="scope-text" style={{
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em'
                }}>Application</span>
              </label>
            </div>

            {/* Dynamic Selector */}
            {scopeType === 'agent' && (
              <select
                className="agent-selector"
                value={selectedAgent}
                onChange={(e) => onSelectAgent(e.target.value)}
              >
                <option value="">Select Agent...</option>
                {agents.filter(a => a.isActive).map(agent => (
                  <option key={agent.id} value={agent.name}>
                    {agent.name} ({agent.testCaseCount} tests)
                  </option>
                ))}
              </select>
            )}

            {scopeType === 'api' && (
              <select
                className="agent-selector"
                value={selectedAPI}
                onChange={(e) => { if (onSelectAPI) onSelectAPI(e.target.value); }}
              >
                <option value="">Select API...</option>
                {apis.map(api => (
                  <option key={api.id} value={api.name}>
                    {api.name} ({api.testCount} tests)
                  </option>
                ))}
              </select>
            )}

            {scopeType === 'application' && (
              <select
                className="agent-selector"
                value={selectedApplication}
                onChange={(e) => { if (onSelectApplication) onSelectApplication(e.target.value); }}
              >
                <option value="">Select Application...</option>
                {applications.map(app => (
                  <option key={app.id} value={app.name}>
                    {app.name} ({app.agentCount} agents, {app.apiCount} APIs)
                  </option>
                ))}
              </select>
            )}

            {/* Management Buttons */}
            <div className="agent-control-grid">
              {scopeType === 'agent' && (
                <>
                  <button
                    className="pushable push-primary btn-sm"
                    onClick={() => onActionClick('upload-agent')}
                    title="Upload New Agent"
                  >
                    <span className="shadow"></span>
                    <span className="edge"></span>
                    <span className="front">Upload Agent</span>
                  </button>
                  <button
                    className="pushable push-primary btn-sm"
                    onClick={() => onActionClick('delete-agent')}
                    title="Delete Selected Agent"
                    disabled={!selectedAgent}
                  >
                    <span className="shadow"></span>
                    <span className="edge"></span>
                    <span className="front">Delete Agent</span>
                  </button>
                </>
              )}

              {scopeType === 'api' && (
                <>
                  <button
                    className="pushable push-primary btn-sm"
                    onClick={() => onActionClick('view-apis')}
                    title="View Registered APIs"
                  >
                    <span className="shadow"></span>
                    <span className="edge"></span>
                    <span className="front">⚙️ Manage</span>
                  </button>
                  <button
                    className="pushable push-primary btn-sm"
                    onClick={() => onActionClick('create-api')}
                    title="Register New API"
                  >
                    <span className="shadow"></span>
                    <span className="edge"></span>
                    <span className="front">+ New API</span>
                  </button>
                </>
              )}

              {scopeType === 'application' && (
                <>
                  <button
                    className="pushable push-primary btn-sm"
                    onClick={() => onActionClick('view-app')}
                    title="View Application Details"
                    disabled={!selectedApplication}
                  >
                    <span className="shadow"></span>
                    <span className="edge"></span>
                    <span className="front">View App</span>
                  </button>
                  <button
                    className="pushable push-primary btn-sm"
                    onClick={() => onActionClick('manage-app')}
                    title="Manage Application"
                  >
                    <span className="shadow"></span>
                    <span className="edge"></span>
                    <span className="front">⚙️ Manage</span>
                  </button>
                </>
              )}
            </div>

            <button className="pushable push-primary btn-sm" onClick={() => onActionClick('document-processor')} title="Document Processor">
              <span className="shadow"></span><span className="edge"></span><span className="front">Document Management</span>
            </button>


          </div>

          {/* ✅ ADD THIS NEW INTEGRATIONS SECTION */}
          <div className="hub-section">
            <h3 className="hub-title">INTEGRATIONS</h3>
            <div className="action-grid">
              <button
                className="pushable push-primary btn-sm"
                onClick={() => onActionClick('manage-containers')}
                title="Manage API Containers"
              >
                <span className="shadow"></span>
                <span className="edge"></span>
                <span className="front">📦 Containers</span>
              </button>

              <button
                className="pushable push-primary btn-sm"
                onClick={() => onActionClick('create-api')}
                title="Create API Endpoint"
              >
                <span className="shadow"></span>
                <span className="edge"></span>
                <span className="front">🔌 Create API</span>
              </button>

              <button
                className="pushable push-primary btn-sm"
                onClick={() => onActionClick('view-apis')}
                title="View All APIs"
              >
                <span className="shadow"></span>
                <span className="edge"></span>
                <span className="front">👁️ View APIs</span>
              </button>
            </div>
          </div>


          {/* 2. Actions */}
          <div className="hub-section primary-actions">
            <h3 className="hub-title">EXECUTE</h3>
            <div className="action-grid">
              <button className="pushable push-primary btn-sm" onClick={() => onActionClick('generator')} title="Create Tests">
                <span className="shadow"></span><span className="edge"></span><span className="front">Create Tests</span>
              </button>

              <button className="pushable push-primary btn-sm" onClick={() => onActionClick('execute')} disabled={isGeneratingTests || isTestRunning} title="Execute Tests">
                <span className="shadow"></span><span className="edge"></span><span className="front">Execute</span>
              </button>
              <button className="pushable push-primary btn-sm" onClick={() => onActionClick('results')} title="Test Progress">
                <span className="shadow"></span><span className="edge"></span><span className="front">Test Progress</span>
              </button>
            </div>
          </div>

          {/* 3. Reports */}
          <div className="hub-section secondary-actions">
            <h3 className="hub-title">Reports</h3>
            <div className="tools-grid">
              <button className="pushable push-primary btn-sm1" onClick={() => onActionClick('database')} title="Database">
                <span className="shadow"></span><span className="edge"></span><span className="front">DB</span>
              </button>
              <button className="pushable push-primary btn-sm1" onClick={() => onActionClick('data')} title="Test Data">
                <span className="shadow"></span><span className="edge"></span><span className="front">Test Data</span>
              </button>
              <button className="pushable push-primary btn-sm1" onClick={() => onActionClick('report')} title="Generate Reports">
                <span className="shadow"></span><span className="edge"></span><span className="front">Reports</span>
              </button>
              <button className="pushable push-primary btn-sm1" onClick={() => onActionClick('defects')} title="View Defects">
                <span className="shadow"></span><span className="edge"></span><span className="front">Defects</span>
              </button>
              <button className="pushable push-primary btn-sm1" onClick={() => onActionClick('scenarios')} title="AI Test Scenarios">
                <span className="shadow"></span><span className="edge"></span><span className="front">Scenarios</span>
              </button>
              <button className="pushable push-primary btn-sm1" onClick={() => onActionClick('dashboard')} title="Analytics Dashboard">
                <span className="shadow"></span><span className="edge"></span><span className="front">Dashboard</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

};

// Helper function to get icons
const getActionIcon = (action: string): string => {
  const icons: { [key: string]: string } = {
    'scenarios': '🎯',
    'execute': '▶️',
    'data': '📊',
    'report': '📋',
    'defects': '🐞',
    'stop-tests': '🛑',
    'view-logs': '📜',
    'view-progress': '📈'
  };
  return icons[action] || '❓';
};

// Helper function to get readable labels
const getActionLabel = (action: string): string => {
  const labels: { [key: string]: string } = {
    'upload-agent': 'Upload Agent',
    'scenarios': 'AI Scenarios',
    'execute': 'Execute',
    'data': 'Test Data',
    'report': 'Reports',
    'defects': 'Defects',
    'stop-tests': 'Stop Tests',
    'view-logs': 'View Logs',
    'view-progress': 'Progress',
    'results': 'Results',
    'ai-config': 'AI Config',
    'environment': 'Environment',
    'logs': 'Logs',
    'dashboard': 'Dashboard',
    'generator': 'Generator',
    'admin': 'Admin'
  };
  return labels[action] || action;
};

export default CommandHub;